import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight, ArrowLeft, CheckCircle2, User, Phone, Mail, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIMULATOR_BG = "https://images.unsplash.com/photo-1757439402190-99b73ac8e807?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3NzI2MzYwNjZ8MA&ixlib=rb-4.1.0&q=85";

const dispositifs = [
  { value: "pinel", label: "Pinel / Pinel+", description: "Neuf - Réduction jusqu'à 14%" },
  { value: "denormandie", label: "Denormandie", description: "Ancien - Réduction jusqu'à 21%" },
  { value: "jeanbrun", label: "Jeanbrun", description: "Neuf/Ancien - Amortissement 3-5,5%/an" },
  { value: "locavantages", label: "Loc'Avantages", description: "Ancien - Réduction sur loyers jusqu'à 65%" },
  { value: "deficit_foncier", label: "Déficit Foncier", description: "Ancien - Déduction travaux" },
  { value: "lmnp", label: "LMNP", description: "Meublé - Amortissement" },
  { value: "malraux", label: "Malraux", description: "Patrimoine - Réduction 22-30%" },
  { value: "monuments_historiques", label: "Monuments Historiques", description: "Classé - 100% déductible" },
];

const durees = [
  { value: 6, label: "6 ans" },
  { value: 9, label: "9 ans" },
  { value: 12, label: "12 ans" },
];

const niveauxLoyer = [
  { value: "intermediaire", label: "Intermédiaire (-15 à -20%)" },
  { value: "social", label: "Social (-30%)" },
  { value: "tres_social", label: "Très social (-45%)" },
];

const niveauxLocAvantages = [
  { value: "loc1", label: "Loc1 (-15% loyer)" },
  { value: "loc2", label: "Loc2 (-30% loyer)" },
  { value: "loc3", label: "Loc3 (-45% loyer)" },
];

const Simulateur = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Contact
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    accepteContact: false,
    // Step 2: Simulation
    dispositif: "",
    montantInvestissement: 200000,
    dureeEngagement: 9,
    niveauLoyer: "intermediaire",
    zone: "B1",
  });

  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = "Le nom est requis";
    if (!formData.prenom.trim()) newErrors.prenom = "Le prénom est requis";
    if (!formData.telephone.trim()) newErrors.telephone = "Le téléphone est requis";
    else if (!/^[0-9]{10}$/.test(formData.telephone.replace(/\s/g, ""))) {
      newErrors.telephone = "Numéro de téléphone invalide";
    }
    if (!formData.email.trim()) newErrors.email = "L'email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email invalide";
    }
    if (!formData.accepteContact) {
      newErrors.accepteContact = "Vous devez accepter d'être recontacté";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.dispositif) newErrors.dispositif = "Sélectionnez un dispositif";
    if (formData.montantInvestissement < 50000) {
      newErrors.montantInvestissement = "Montant minimum: 50 000€";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    if (step === 3) setStep(2);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    setLoading(true);
    try {
      // First, create the lead
      const leadData = {
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.telephone,
        email: formData.email,
        accepte_contact: formData.accepteContact,
        dispositif_interesse: formData.dispositif,
        montant_investissement: formData.montantInvestissement,
      };

      await axios.post(`${API}/leads`, leadData);

      // Then, run simulation
      const simulationData = {
        dispositif: formData.dispositif,
        montant_investissement: formData.montantInvestissement,
        duree_engagement: formData.dureeEngagement,
        zone: formData.zone,
        niveau_loyer: formData.dispositif === "locavantages" ? formData.niveauLoyer : formData.niveauLoyer,
      };

      const response = await axios.post(`${API}/simulate`, simulationData);
      setResult(response.data);

      // Update lead with simulation result
      setStep(3);
      toast.success("Simulation effectuée avec succès !");
    } catch (error) {
      console.error("Simulation error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const needsNiveauLoyer = ["jeanbrun", "locavantages"].includes(formData.dispositif);
  const needsDuree = ["pinel", "denormandie"].includes(formData.dispositif);

  return (
    <Layout>
      {/* Background */}
      <div
        className="min-h-screen pt-24 pb-16 relative"
        style={{
          backgroundImage: `url(${SIMULATOR_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="simulateur-page"
      >
        <div className="absolute inset-0 bg-harbor/60 backdrop-blur-sm" />

        <div className="container-custom relative z-10">
          {/* Header */}
          <div className="text-center text-white mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 animate-fade-in-up">
              Simulateur Fiscal
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
              Estimez votre réduction d'impôt en quelques clics
            </p>
          </div>

          {/* Step Indicator */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`step-dot ${step >= s ? (step > s ? "completed" : "active") : ""}`}
                    data-testid={`step-indicator-${s}`}
                  />
                  {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-growth" : "bg-white/30"}`} />}
                </div>
              ))}
            </div>
          </div>

          {/* Simulator Card */}
          <div className="max-w-2xl mx-auto">
            <Card className="simulator-glass animate-fade-in-up" data-testid="simulator-card">
              {/* Step 1: Contact Info */}
              {step === 1 && (
                <>
                  <CardHeader className="text-center pb-2">
                    <div className="mx-auto p-3 bg-harbor/10 rounded-full w-fit mb-4">
                      <User className="text-harbor" size={28} />
                    </div>
                    <CardTitle className="font-heading text-2xl text-harbor">
                      Vos coordonnées
                    </CardTitle>
                    <p className="text-muted-foreground text-sm mt-2">
                      Renseignez vos informations pour recevoir votre simulation personnalisée
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-5 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nom" className="text-base font-medium text-harbor">Nom *</Label>
                        <Input
                          id="nom"
                          value={formData.nom}
                          onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                          className={`h-12 text-base ${errors.nom ? "border-red-500" : ""}`}
                          placeholder="Dupont"
                          data-testid="input-nom"
                        />
                        {errors.nom && <p className="text-red-500 text-sm mt-1">{errors.nom}</p>}
                      </div>
                      <div>
                        <Label htmlFor="prenom" className="text-base font-medium text-harbor">Prénom *</Label>
                        <Input
                          id="prenom"
                          value={formData.prenom}
                          onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                          className={`h-12 text-base ${errors.prenom ? "border-red-500" : ""}`}
                          placeholder="Jean"
                          data-testid="input-prenom"
                        />
                        {errors.prenom && <p className="text-red-500 text-sm mt-1">{errors.prenom}</p>}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="telephone" className="text-base font-medium text-harbor">Téléphone *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                        <Input
                          id="telephone"
                          value={formData.telephone}
                          onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                          className={`pl-11 h-12 text-base ${errors.telephone ? "border-red-500" : ""}`}
                          placeholder="06 12 34 56 78"
                          data-testid="input-telephone"
                        />
                      </div>
                      {errors.telephone && <p className="text-red-500 text-sm mt-1">{errors.telephone}</p>}
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-base font-medium text-harbor">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className={`pl-11 h-12 text-base ${errors.email ? "border-red-500" : ""}`}
                          placeholder="jean.dupont@email.com"
                          data-testid="input-email"
                        />
                      </div>
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div className="flex items-start gap-3 pt-2">
                      <Checkbox
                        id="accepteContact"
                        checked={formData.accepteContact}
                        onCheckedChange={(checked) => setFormData({ ...formData, accepteContact: checked })}
                        data-testid="checkbox-accepte"
                      />
                      <Label htmlFor="accepteContact" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                        J'accepte d'être recontacté par Inter'med Immobilier pour discuter de mon projet d'investissement. *
                      </Label>
                    </div>
                    {errors.accepteContact && <p className="text-red-500 text-xs">{errors.accepteContact}</p>}

                    <Button
                      onClick={handleNext}
                      className="w-full bg-accent hover:bg-accent/90 text-white mt-4"
                      data-testid="btn-next-step1"
                    >
                      Continuer
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </CardContent>
                </>
              )}

              {/* Step 2: Simulation Parameters */}
              {step === 2 && (
                <>
                  <CardHeader className="text-center pb-2">
                    <div className="mx-auto p-3 bg-harbor/10 rounded-full w-fit mb-4">
                      <Building2 className="text-harbor" size={28} />
                    </div>
                    <CardTitle className="font-heading text-2xl text-harbor">
                      Votre projet
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">
                      Configurez les paramètres de votre investissement
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-4">
                    <div>
                      <Label>Dispositif fiscal *</Label>
                      <Select
                        value={formData.dispositif}
                        onValueChange={(value) => setFormData({ ...formData, dispositif: value })}
                      >
                        <SelectTrigger className={errors.dispositif ? "border-red-500" : ""} data-testid="select-dispositif">
                          <SelectValue placeholder="Sélectionnez un dispositif" />
                        </SelectTrigger>
                        <SelectContent>
                          {dispositifs.map((d) => (
                            <SelectItem key={d.value} value={d.value} data-testid={`option-${d.value}`}>
                              <div>
                                <div className="font-medium">{d.label}</div>
                                <div className="text-xs text-muted-foreground">{d.description}</div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.dispositif && <p className="text-red-500 text-xs mt-1">{errors.dispositif}</p>}
                    </div>

                    <div>
                      <Label>Montant d'investissement: {formatCurrency(formData.montantInvestissement)}</Label>
                      <Slider
                        value={[formData.montantInvestissement]}
                        onValueChange={([value]) => setFormData({ ...formData, montantInvestissement: value })}
                        min={50000}
                        max={1000000}
                        step={10000}
                        className="mt-3"
                        data-testid="slider-montant"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>50 000 €</span>
                        <span>1 000 000 €</span>
                      </div>
                    </div>

                    {needsDuree && (
                      <div>
                        <Label>Durée d'engagement</Label>
                        <Select
                          value={String(formData.dureeEngagement)}
                          onValueChange={(value) => setFormData({ ...formData, dureeEngagement: parseInt(value) })}
                        >
                          <SelectTrigger data-testid="select-duree">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {durees.map((d) => (
                              <SelectItem key={d.value} value={String(d.value)}>
                                {d.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {needsNiveauLoyer && (
                      <div>
                        <Label>Niveau de loyer</Label>
                        <Select
                          value={formData.niveauLoyer}
                          onValueChange={(value) => setFormData({ ...formData, niveauLoyer: value })}
                        >
                          <SelectTrigger data-testid="select-niveau">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(formData.dispositif === "locavantages" ? niveauxLocAvantages : niveauxLoyer).map((n) => (
                              <SelectItem key={n.value} value={n.value}>
                                {n.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="flex gap-4 pt-4">
                      <Button
                        variant="outline"
                        onClick={handleBack}
                        className="flex-1"
                        data-testid="btn-back-step2"
                      >
                        <ArrowLeft className="mr-2" size={18} />
                        Retour
                      </Button>
                      <Button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex-1 bg-accent hover:bg-accent/90 text-white"
                        data-testid="btn-simulate"
                      >
                        {loading ? "Calcul en cours..." : "Calculer ma réduction"}
                        <Calculator className="ml-2" size={18} />
                      </Button>
                    </div>
                  </CardContent>
                </>
              )}

              {/* Step 3: Results */}
              {step === 3 && result && (
                <>
                  <CardHeader className="text-center pb-2">
                    <div className="mx-auto p-3 bg-growth/10 rounded-full w-fit mb-4">
                      <CheckCircle2 className="text-growth" size={28} />
                    </div>
                    <CardTitle className="font-heading text-2xl text-harbor">
                      Votre simulation
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">
                      Estimation de votre avantage fiscal
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-4" data-testid="simulation-results">
                    {/* Main result */}
                    <div className="text-center p-6 bg-growth/10 rounded-lg">
                      <div className="text-sm text-muted-foreground mb-1">Réduction d'impôt totale estimée</div>
                      <div className="text-4xl font-heading text-growth font-bold" data-testid="result-total">
                        {formatCurrency(result.reduction_totale)}
                      </div>
                      <div className="text-sm text-muted-foreground mt-2">
                        soit {result.taux_reduction.toFixed(1)}% de votre investissement
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-3">
                      <div className="flex justify-between p-3 bg-gray-50 rounded">
                        <span className="text-muted-foreground">Dispositif</span>
                        <span className="font-semibold capitalize">{result.dispositif.replace("_", " ")}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-gray-50 rounded">
                        <span className="text-muted-foreground">Investissement</span>
                        <span className="font-semibold">{formatCurrency(result.montant_investissement)}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-gray-50 rounded">
                        <span className="text-muted-foreground">Réduction annuelle</span>
                        <span className="font-semibold text-growth" data-testid="result-annuel">{formatCurrency(result.reduction_annuelle)}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-gray-50 rounded">
                        <span className="text-muted-foreground">Durée d'engagement</span>
                        <span className="font-semibold">{result.duree_engagement} ans</span>
                      </div>
                    </div>

                    {/* Details explanation */}
                    <div className="p-4 bg-harbor/5 rounded-lg border border-harbor/10">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {result.details}
                      </p>
                    </div>

                    {/* Disclaimer */}
                    <p className="text-xs text-muted-foreground text-center">
                      * Cette simulation est indicative et ne constitue pas un conseil fiscal.
                      Un conseiller Inter'med Immobilier vous contactera pour affiner votre projet.
                    </p>

                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        onClick={() => setStep(2)}
                        className="flex-1"
                        data-testid="btn-new-simulation"
                      >
                        Nouvelle simulation
                      </Button>
                      <Button
                        onClick={() => navigate("/contact")}
                        className="flex-1 bg-accent hover:bg-accent/90 text-white"
                        data-testid="btn-contact-after"
                      >
                        Être recontacté
                      </Button>
                    </div>
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Simulateur;
